import { useState } from 'react'
import { Button } from '@/components/ui/Button'
import { Modal } from '@/components/ui/Modal'
import type { AllowRow, AllowState, CapabilityException, FleetColumn } from '@/lib/reference'
import { exceptionDiff } from '@/lib/reference/fleet'

/** Same cycle as the matrix, so a cell behaves the same wherever it is. */
const NEXT: Record<AllowState, AllowState> = { yes: 'no', no: 'unset', unset: 'yes' }
const GLYPH: Record<AllowState, string> = { yes: '✓', no: '✕', unset: '—' }
const TONE: Record<AllowState, string> = {
  yes: 'text-semantic-up',
  no: 'text-semantic-down',
  unset: 'text-muted-soft',
}

/**
 * Ad-hoc exception against one city pair, country or airport.
 *
 * The Capability screen owns the baseline; this owns the departures from it.
 * Both states are on screen at once because the difference is the point: an
 * exception that looks like an ordinary setting is one nobody will remember
 * to take back off.
 *
 * A cell cycled back to the baseline is not saved as an exception, and a
 * dialog left with no differences removes the exception altogether.
 */
export function ExceptionDialog({
  subject,
  subtitle,
  columns,
  baseline,
  current,
  canEdit,
  onClose,
  onSave,
}: {
  subject: string
  subtitle?: string
  columns: FleetColumn[]
  /** Baseline verdict per column, with DEFAULT already applied. */
  baseline: (column: string) => AllowState
  current?: CapabilityException
  canEdit: boolean
  onClose: () => void
  /** An empty `allow` means: drop the exception. */
  onSave: (allow: AllowRow, reason: string) => Promise<void>
}) {
  const [draft, setDraft] = useState<AllowRow>(() => {
    const start: AllowRow = {}
    for (const column of columns) start[column.key] = baseline(column.key)
    return { ...start, ...current?.allow }
  })
  const [reason, setReason] = useState(current?.reason ?? '')
  const [saving, setSaving] = useState(false)

  const diff = exceptionDiff(draft, baseline, columns)
  const differences = Object.keys(diff).length
  const stored = Object.keys(current?.allow ?? {}).length
  const dirty = differences !== stored || reason !== (current?.reason ?? '')

  function commit(allow: AllowRow, note: string) {
    setSaving(true)
    onSave(allow, note)
      .then(onClose)
      .catch(() => setSaving(false))
  }

  return (
    <Modal title="Exception" onClose={onClose} width="520px">
      <p className="t-body-md mt-1 text-body">
        <span className="t-number-sm text-ink">{subject}</span>
        {subtitle && ` · ${subtitle}`}
      </p>

      <ul className="mt-6 divide-y divide-hairline-soft border-y border-hairline-soft">
        {columns.map((column) => {
          const base = baseline(column.key)
          const state = draft[column.key] ?? base
          const changed = state !== base
          return (
            <li key={column.key} className="flex items-center justify-between gap-3 py-2.5">
              <span className="min-w-0">
                <span className="t-body-sm block truncate text-ink">
                  {column.group} · {column.type}
                </span>
                <span className="t-number-sm text-muted">
                  {column.category} · {column.tails}
                </span>
              </span>

              <span className="flex items-center gap-2 whitespace-nowrap">
                {changed && (
                  <>
                    <span className={`t-title-sm ${TONE[base]}`} aria-hidden="true">
                      {GLYPH[base]}
                    </span>
                    <span className="t-caption text-muted-soft" aria-hidden="true">
                      →
                    </span>
                  </>
                )}
                <button
                  type="button"
                  disabled={!canEdit}
                  aria-label={`${column.key}: ${state}`}
                  onClick={() =>
                    setDraft((value) => ({ ...value, [column.key]: NEXT[state] }))
                  }
                  className={`t-title-sm h-8 w-8 rounded-[8px] transition-colors
                              enabled:hover:bg-surface-strong enabled:active:bg-hairline
                              ${TONE[state]} ${changed ? 'ring-1 ring-ink' : ''}`}
                >
                  {GLYPH[state]}
                </button>
              </span>
            </li>
          )
        })}
      </ul>

      <div className="mt-6">
        <label htmlFor="exception-reason" className="t-title-sm mb-2 block text-ink">
          Reason
        </label>
        <input
          id="exception-reason"
          type="text"
          value={reason}
          disabled={!canEdit}
          onChange={(event) => setReason(event.target.value)}
          className="t-body-md h-12 w-full rounded-[12px] border border-hairline bg-canvas px-4
                     text-ink outline-none focus:border-2 focus:border-primary focus:px-[15px]"
        />
      </div>

      <p className="t-caption mt-4 text-muted">
        <span className="t-number-sm text-ink">{differences}</span> of {columns.length} overridden
      </p>

      {canEdit ? (
        <div className="mt-6 flex flex-wrap gap-3">
          <Button
            className="flex-1"
            loading={saving}
            disabled={!dirty}
            onClick={() => commit(diff, reason.trim())}
          >
            Save
          </Button>
          <Button variant="secondary" className="flex-1" onClick={onClose}>
            Cancel
          </Button>
          {stored > 0 && (
            <Button
              variant="secondary"
              fullWidth
              className="text-semantic-down"
              disabled={saving}
              onClick={() => commit({}, '')}
            >
              Remove
            </Button>
          )}
        </div>
      ) : (
        <div className="mt-6">
          <Button variant="secondary" fullWidth onClick={onClose}>
            Close
          </Button>
        </div>
      )}
    </Modal>
  )
}
